import { RECENTLY_INSERTED_DAYS, insertedWindowStart } from './libraryFilterOptions.ts';
import { DEFAULT_LIBRARY_FILTER_STATE, type LibraryFilterState } from './libraryFilterState.ts';
import type { LibraryItem } from '../../../shared/api.ts';

/**
 * The Library rail's Views section, as data.
 *
 * Written to `CategorySidebar`'s row contract, because the sidebar is shared
 * and knows nothing about either page (the decks page's rail is folders only
 * since 2026-09-07): a row is a name, a tally, whether it is the current one,
 * and a patch to apply when it is clicked.
 *
 * THE RULE THAT DECIDED THE SHAPE OF BOTH ROWS is Run 4's, learned on the decks
 * page: **a row that shows a count must show exactly what it counts.** Recently
 * opened started life as a re-sort, which meant the row read 8 above a grid of
 * 29 — a control you only stop trusting after believing it once. So neither of
 * these rows is a sort alone:
 *
 * - **Recently inserted** narrows on the `inserted` axis (a 30-day window over
 *   `lastInsertedAt`, see `RECENTLY_INSERTED_DAYS`) and orders by it. Its count
 *   is the number of items inside that window.
 * - **Most used** narrows on the EXISTING `used` axis (at least one deck holds
 *   a copy) and orders by the insert tally. Its count is the number of items
 *   any deck has taken. Sorting alone has no cardinality, so a "Most used" that
 *   only reordered could carry no honest number at all.
 *
 * Both counts are ZERO on a studio whose usage registry (Run 8) is younger than
 * its library, and they fill in as the library gets used. That is not a fault
 * and the empty states say so — see `libraryEmptyState.ts`.
 */

/** The views that exist. Stable ids: the rail keys rows and picks icons by
 *  them, and the headless UI check names them. */
export type LibraryViewId = 'all' | 'inserted' | 'used';

export type LibraryViewRow = {
  id: LibraryViewId;
  label: string;
  count: number;
  /** True when the current filter state IS this view. At most one row. */
  active: boolean;
  /** What clicking the row writes into the filter state. A patch, so grouping
   *  and anything a view says nothing about survive the click. */
  patch: Partial<LibraryFilterState>;
};

type LibraryViewsInput = {
  /** The whole shelf, unfiltered — the counts describe what the library HOLDS,
   *  which is what makes them a promise the body can keep. */
  items: readonly LibraryItem[];
  filters: LibraryFilterState;
  /** Injectable clock: the window is relative and a test cannot assert on "the
   *  last 30 days" without pinning today. */
  now?: number;
};

/** The narrowing axes a view owns. Query is included for the decks page's
 *  reason: a view row is a "show me this slice" instruction, and leaving a live
 *  search behind would make the row claim a count the body contradicts. */
const CLEARED: Partial<LibraryFilterState> = {
  query: DEFAULT_LIBRARY_FILTER_STATE.query,
  type: null,
  category: null,
  used: null,
  inserted: null,
};

/** Is nothing narrowing the body? All saved slides is lit exactly then. */
function unnarrowed(f: LibraryFilterState): boolean {
  return f.query === '' && f.type === null && f.category === null && f.used === null && f.inserted === null;
}

/** How many DECKS hold a copy. Absent on a client cached across the Run 8
 *  deploy; 0 is the honest answer for an item nobody has used. */
function usedInDecks(item: LibraryItem): number {
  return item.usedInDecks ?? 0;
}

export function libraryViewRows({ items, filters, now }: LibraryViewsInput): LibraryViewRow[] {
  // The same window the `inserted` axis uses, so the number on the row and the
  // number of cards under it cannot drift apart.
  const since = insertedWindowStart('recent', now ?? Date.now()) as number;
  const insertedCount = items.filter(
    (it) => typeof it.lastInsertedAt === 'number' && it.lastInsertedAt >= since,
  ).length;
  const usedCount = items.filter((it) => usedInDecks(it) > 0).length;

  return [
    {
      id: 'all',
      // "All saved slides", not "All items": the rail is read beside the Bank
      // tab, whose rows are assets, and this row is the one place on the page
      // that has to say which of the two shelves it is talking about.
      label: 'All saved slides',
      count: items.length,
      active: unnarrowed(filters),
      patch: { ...CLEARED, sort: DEFAULT_LIBRARY_FILTER_STATE.sort },
    },
    {
      id: 'inserted',
      label: 'Recently inserted',
      count: insertedCount,
      active: filters.inserted === 'recent',
      patch: { ...CLEARED, inserted: 'recent', sort: 'inserted' },
    },
    {
      id: 'used',
      label: 'Most used',
      count: usedCount,
      active: filters.used === 'used',
      patch: { ...CLEARED, used: 'used', sort: 'used' },
    },
  ];
}

/** The lit row, if any. The phone rail's collapsed summary names it, since
 *  "All saved slides" is a lie while Most used is the current view. */
export function activeLibraryView(rows: LibraryViewRow[]): LibraryViewRow | null {
  return rows.find((r) => r.active) ?? null;
}

/** Re-exported so the empty state and the rail read one number for the
 *  window rather than each writing 30. */
export { RECENTLY_INSERTED_DAYS };
